import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import Skeleton from '@mlb/react-patterns/components/Skeleton/skeleton.tsx';
import COLORS from '@mlb/react-patterns/style/universal.color';
import { ReferenceWrapper } from '@mlb/react-patterns/components/Popover/popover.style.ts';

import { IMAGE_PLACEHOLDER_SVG } from '../../utils/constants';

import CardPattern from '../AspectRatioCard';
import PlayIcon from '../PlayIcon';
import ReelCheck from '../ReelCheck';

const BASE_WIDTH = 343;
const BASE_HEIGHT = 229;
const BOX_PADDING = 16;

export const Card = styled(CardPattern)`
    ${({ disabled }) => disabled && `
        pointer-events: none;
    `}
`;

const BackgroundImage = styled.img`
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
    mask: linear-gradient(0deg, rgba(15, 15, 15, 0) 15.06%, #0F0F0F 92.61%);
`;

const Wrapper = styled.div`
    background: transparent;
    display: flex;
    position: absolute;
    top: calc(100% - 70px);
    width: 100%;
`;

const PlayIconOverride = styled(PlayIcon)`
    margin-left: ${BOX_PADDING}px;
`;

export const TextWrapper = styled.div`
    font-family: ${({ theme }) => theme.fonts.family.primary};
    padding: 0 ${BOX_PADDING}px;
    display: flex;
    flex-direction: column;
    justify-content: center;
`;

export const P = styled.p`
    color: ${({ theme }) => theme.primaryTextColor};
    margin: 0;
    padding: 0;
`;

const Title = styled(P)`
    font-size: 17px;
    font-weight: 600;
    line-height: 21px;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    overflow-wrap: anywhere;
`;

const Date = styled(P)`
    font-size: 12px;
    color: ${COLORS.mlbGrey3};
`;

const OptionsWrapper = styled.div`
    margin-left: auto;
    ${ReferenceWrapper} {
        height: 40px;
    }
`;

const HeroCard = (props) => {

    const {
        as,
        date,
        optionsComponent,
        imageCuts,
        loading,
        title,
        isInReel,
        ...otherProps
    } = props;

    const imageSrc = imageCuts[0]?.src ?? IMAGE_PLACEHOLDER_SVG;
    const imageSrcSet = imageCuts.map((img) => `${img.src} ${img.width}w`).join(', ');

    return (
        <Card
            forwardedAs={as}
            aspectRatio={`${BASE_WIDTH}:${BASE_HEIGHT}`}
            {...otherProps}
        >
            {loading ? (
                <Skeleton margin={0} />
            ): (
                <>
                    <BackgroundImage src={imageSrc} srcSet={imageSrcSet} loading="lazy" />
                    <Wrapper>
                        <PlayIconOverride width={40} />
                        <TextWrapper>
                            <Title>{title}</Title>
                            <Date>{date}</Date>
                        </TextWrapper>
                        {optionsComponent && (
                            <OptionsWrapper>{optionsComponent}</OptionsWrapper>
                        )}
                    </Wrapper>
                </>
            )}
            {isInReel && <ReelCheck />}
        </Card>
    );

};

HeroCard.propTypes = {
    date: PropTypes.string,
    imageCuts: PropTypes.arrayOf(
        PropTypes.shape({
            src: PropTypes.string.isRequired,
            width: PropTypes.number.isRequired,
        })
    ),
    optionsComponent: PropTypes.node,
    title: PropTypes.string,
    loading: PropTypes.bool,
    isInReel: PropTypes.bool,
    disabled: PropTypes.bool,
    as: PropTypes.oneOfType([
        PropTypes.object,
        PropTypes.node,
    ]),
};

HeroCard.defaultProps = {
    loading: false,
    imageCuts: [],
    isInReel: false,
    disabled: false,
};


export default HeroCard;
